import { create } from 'zustand';
import type { ActiveFilter } from '../models/ViewRules';

export interface SmartGroup {
  id: string;
  name: string;
  filter: ActiveFilter;
  createdAt: Date;
  updatedAt: Date;
}

const STORAGE_KEY = 'creately_assistant_smart_groups';

const loadSmartGroups = (diagramId: string): SmartGroup[] => {
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY}_${diagramId}`);
    if (!stored) return [];
    const parsed = JSON.parse(stored) as Array<Omit<SmartGroup, 'createdAt' | 'updatedAt'> & {
      createdAt: string;
      updatedAt: string;
    }>;
    return parsed.map((g) => ({
      ...g,
      createdAt: new Date(g.createdAt),
      updatedAt: new Date(g.updatedAt),
    }));
  } catch {
    return [];
  }
};

const saveSmartGroups = (diagramId: string, groups: SmartGroup[]): void => {
  try {
    localStorage.setItem(`${STORAGE_KEY}_${diagramId}`, JSON.stringify(groups));
  } catch {
    // Storage full or unavailable
  }
};

interface SmartGroupsState {
  diagramId: string | null;
  smartGroups: SmartGroup[];

  initialize: (diagramId: string) => void;
  createSmartGroup: (name: string, filter: ActiveFilter) => SmartGroup;
  renameSmartGroup: (id: string, name: string) => void;
  updateSmartGroupFilter: (id: string, filter: ActiveFilter) => void;
  deleteSmartGroup: (id: string) => void;
  getSmartGroup: (id: string) => SmartGroup | undefined;
}

export const useSmartGroupsStore = create<SmartGroupsState>((set, get) => ({
  diagramId: null,
  smartGroups: [],

  initialize: (diagramId) => {
    const smartGroups = loadSmartGroups(diagramId);
    set({ diagramId, smartGroups });
  },

  createSmartGroup: (name, filter) => {
    const { diagramId, smartGroups } = get();
    const newGroup: SmartGroup = {
      id: `smart-group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name,
      filter,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    const updatedGroups = [...smartGroups, newGroup];
    set({ smartGroups: updatedGroups });
    if (diagramId) saveSmartGroups(diagramId, updatedGroups);
    return newGroup;
  },

  renameSmartGroup: (id, name) => {
    const { diagramId, smartGroups } = get();
    const updatedGroups = smartGroups.map((g) =>
      g.id === id ? { ...g, name, updatedAt: new Date() } : g
    );
    set({ smartGroups: updatedGroups });
    if (diagramId) saveSmartGroups(diagramId, updatedGroups);
  },

  updateSmartGroupFilter: (id, filter) => {
    const { diagramId, smartGroups } = get();
    const updatedGroups = smartGroups.map((g) =>
      g.id === id ? { ...g, filter, updatedAt: new Date() } : g
    );
    set({ smartGroups: updatedGroups });
    if (diagramId) saveSmartGroups(diagramId, updatedGroups);
  },

  deleteSmartGroup: (id) => {
    const { diagramId, smartGroups } = get();
    const updatedGroups = smartGroups.filter((g) => g.id !== id);
    set({ smartGroups: updatedGroups });
    if (diagramId) saveSmartGroups(diagramId, updatedGroups);
  },

  getSmartGroup: (id) => get().smartGroups.find((g) => g.id === id),
}));
